import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Divider,
  Alert,
  Checkbox,
  FormControlLabel,
  Paper,
  useTheme,
} from "@mui/material";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import GoogleButton from "react-google-button";

const LoginSection = ({ token }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { googleLoginHandler, currentUser } = useAuth();

  // Effect to handle redirect when user becomes authenticated
  useEffect(() => {
    if (currentUser && token) {
      // If user is authenticated and we have a token, refresh the page to trigger validation
      window.location.reload();
    }
  }, [currentUser, token]);

  const handleGoogleLogin = async () => {
    if (!termsAgreed) {
      setError("Please agree to the terms and conditions before proceeding.");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Store the token before login attempt
      sessionStorage.setItem("pendingClaimToken", token);

      const result = await googleLoginHandler();

      if (result && result.user) {
        // Success - the effect above will handle the redirect
        console.log("Login successful, user:", result.user.email);
      } else {
        throw new Error("Login failed");
      }
    } catch (error) {
      console.error("Google login error:", error);
      setError("Failed to login with Google. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleTermsAgreement = (event) => {
    setTermsAgreed(event.target.checked);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
        height: "100%",
      }}
    >
      <Typography variant="h5" gutterBottom>
        Login to Claim Dive
      </Typography>

      <Paper
        elevation={3}
        sx={{
          p: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          maxWidth: "400px",
        }}
      >
        <Typography variant="body1" sx={{ mb: 3, textAlign: "center" }}>
          To add this dive to your logbook, please log in to your account.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2, width: "100%" }}>
            {error}
          </Alert>
        )}

        <FormControlLabel
          control={
            <Checkbox
              checked={termsAgreed}
              onChange={handleTermsAgreement}
              sx={{
                color: theme.palette.primary.main,
                "&.Mui-checked": {
                  color: theme.palette.primary.main,
                },
              }}
            />
          }
          label={
            <Typography variant="body2">
              I agree to the terms and conditions. I understand that I am
              responsible for my own actions and safety.
            </Typography>
          }
          sx={{ mb: 2, width: "100%" }}
        />

        <GoogleButton
          onClick={handleGoogleLogin}
          disabled={!termsAgreed || loading}
          style={{ width: "100%", opacity: !termsAgreed || loading ? 0.6 : 1 }}
        />

        <Divider sx={{ my: 3, width: "100%" }} />

        <Typography variant="body2" sx={{ textAlign: "center" }}>
          Don't have an account?{" "}
          <Link
            to="/register"
            style={{ color: theme.palette.primary.main }}
            onClick={() => sessionStorage.setItem("pendingClaimToken", token)}
          >
            Register
          </Link>
        </Typography>
      </Paper>
    </Box>
  );
};

export default LoginSection;
